import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector, useDispatch } from 'react-redux';

// app modules: actions
import { getAppliances } from 'actions/homeconnect';

// app modules: components
import Link from 'components/Link';

// app modules: services
import homeConnectApi from 'services/homeconnect';
import { formatBrandName } from 'services/formatter';

import showIntercomMessagesHelper from 'api/helpers/showIntercomMessagesHelper';

// assets
import CssHomeConnectPromoInfo from './HomeConnectPromoInfo.scss';

export default function HomeConnectPromoInfo(props) {
  const { brand } = props;
  useStyles(CssHomeConnectPromoInfo);
  const dispatch = useDispatch();
  const ovenBrands = useSelector(state => state.user.oven_brands);
  const homeConnectEnabled = useSelector(state => state.user.home_connect_enabled);
  const currentPartner = useSelector(
    state => (state.user.subscription ? state.user.subscription.partner : null),
  );
  const primaryOven = useSelector(state => state.homeConnect.primaryOven);
  const pathname = useSelector(state => state.router.pathname);
  const hostname = useSelector(state => state.homeConnect.hostname);
  const clientId = useSelector(state => state.homeConnect.clientId);
  const baseUrl = useSelector(state => state.config.baseUrl);
  const token = useSelector(state => state.user.home_connect_token);
  const loggedIn = useSelector(state => state.user.logged_in);

  useEffect(
    () => {
      if (token && !primaryOven) {
        // The user is linked to HC but we didn't yet discover the oven
        dispatch(getAppliances());
      }
    },
    [token, primaryOven, dispatch],
  );

  const handleContactClick = () => {
    if (window.Intercom) {
      window.Intercom('show');
    } else if (window.intercom) {
      showIntercomMessagesHelper();
    }
  };

  const renderAuthLink = text => {
    if (homeConnectEnabled) {
      // eslint-disable-next-line import/no-named-as-default-member
      const authLink = homeConnectApi.getAuthUrl({ hostname, clientId, baseUrl, pathname });
      return (
        <a href={authLink} className="link link--energized">
          {text}
        </a>
      );
    }
    return (
      <Link to="/user/settings" className="link link--energized">
        {text}
      </Link>
    );
  };

  if (!loggedIn) {
    return null;
  }

  const registerLink = (
    <a
      href="https://www.home-connect.com/global/help-support/set-up"
      target="_blank"
      rel="noopener noreferrer"
      className="link link--energized"
    >
      register for a Home Connect account
    </a>
  );

  let blurb = null;
  const niceBrand = formatBrandName(brand);
  if (token && primaryOven && primaryOven.brand && primaryOven.brand.toLowerCase() === brand) {
    // Home connect and matching oven found
    blurb = (
      <Fragment>
        <p>
          <strong>
            Thank you for pairing your {niceBrand} Home Connect oven to ckbk. Your new {niceBrand}{' '}
            oven contains many exciting cooking modes that can help your unleash your creativity to
            produce even more delicious results.
          </strong>
        </p>
      </Fragment>
    );
  } else if (token) {
    blurb = (
      <Fragment>
        <p>
          <strong>Thank you for pairing your Home Connect account with ckbk.</strong>
        </p>
        <p>
          To start using Home Connect to automatically send instructions from ckbk to your oven, you
          now need to{' '}
          <a
            href="https://www.home-connect.com/global/help-support/set-up"
            target="_blank"
            rel="noopener noreferrer"
            className="link link--energized"
          >
            add your oven to your Home Connect account
          </a>{' '}
          using the Home Connect app on your phone. If you run into any problems,{' '}
          <button onClick={handleContactClick} className="link link--energized">
            contact us
          </button>.
        </p>
      </Fragment>
    );
  } else if (currentPartner === brand) {
    blurb = (
      <Fragment>
        <p>
          <strong>Congratulations on purchasing a {niceBrand} connected oven.</strong>
        </p>

        <p>
          To get the most from ckbk, please {registerLink} and then{' '}
          {renderAuthLink('link your Home Connect account to ckbk')}.
        </p>

        <p>
          Once your accounts have been linked you will be able to send optimized cooking
          instructions directly to your oven with a click.
        </p>

        <p>
          Linking your Home Connect account will also give you an extra 3 months of free Premium
          Membership of ckbk.
        </p>

        <p>
          Your new {niceBrand} oven contains many exciting cooking modes that can help your unleash
          your creativity to produce even more delicious results.
        </p>
      </Fragment>
    );
  } else if (ovenBrands.map(_brand => _brand.toLowerCase()).includes(brand.toLowerCase())) {
    blurb = (
      <Fragment>
        <p>
          <strong>
            If your {niceBrand} oven includes Home Connect, ckbk can send cooking instructions
            directly to the oven via wifi. To get the most from ckbk, please {registerLink} and then{' '}
            {renderAuthLink('link your Home Connect account to ckbk')}.
          </strong>
        </p>

        <p>
          Your {niceBrand} oven contains many exciting cooking modes that can help your unleash your
          creativity to produce even more delicious results.
        </p>
      </Fragment>
    );
  }
  return <div className="home-connect-promo-info">{blurb}</div>;
}

HomeConnectPromoInfo.propTypes = {
  brand: PropTypes.string.isRequired,
};

HomeConnectPromoInfo.defaultProps = {};
